/**
 * analytics, track product events (fire-and-forget).
 * Logs go to POST /api/analytics/event. Non-blocking; errors are swallowed.
 */
import axios from 'axios';
import { isCookieAllowed, readCookieConsent, MAP_EVENTS } from '../components/CookieConsentBanner';
import { sendFathomEvent, setFathomActor, scheduleFathomFlush } from './fathom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONSENT_VERSION = '2';
const SESSION_KEY = 'mid_analytics_sessions';
const DNA_PREFIX = 'mid_analytics_dna_started_';
const ENTRY_POINT_KEY = 'mid_dna_entry_point';
let funnelSubject = { actor: null, userId: null };

// Onboarding entry point (docs/plans/onboarding-one-path.md, Analytics): first
// write wins, so OAuth/register never overwrite a landing source. Storage may
// throw in private mode; every access is guarded.
const ENTRY_POINTS = ['landing_first_home', 'landing_move', 'landing_buy', 'landing_rent', 'landing_mascot', 'register', 'google'];

export function getEntryPoint() {
  try { return localStorage.getItem(ENTRY_POINT_KEY) || null; } catch { return null; }
}

/** register/google only; writes only when nothing is stored. */
export function setEntryPoint(value) {
  if (!ENTRY_POINTS.includes(value) || getEntryPoint()) return;
  try { localStorage.setItem(ENTRY_POINT_KEY, value); } catch { /* private mode */ }
}

/** Reads ?intent= / ?from= once into storage; returns the stored entry point. */
export function captureEntryPoint(search = '') {
  const params = new URLSearchParams(search);
  const fromUrl = { primeira_habitacao: 'landing_first_home', upgrade: 'landing_move', arrendamento: 'landing_rent' }[params.get('intent')]
    || { mascot: 'landing_mascot', comprar: 'landing_buy' }[params.get('from')] || null;
  if (fromUrl) setEntryPoint(fromUrl);
  return getEntryPoint();
}

/** Called after the resultado step. */
export function clearEntryPoint() {
  try { localStorage.removeItem(ENTRY_POINT_KEY); } catch { /* private mode */ }
}

// DNA step/account events: each maps props to the allowed ones, or null to drop.
const DNA_STEPS = ['conversa', 'zona', 'dilemas', 'estilo_vida', 'estilo_visual', 'resultado'];
const DNA_FUNNEL_PROPS = new Map([
  ['dna_step_viewed', (p) => (DNA_STEPS.includes(p.step) ? { step: p.step } : null)],
  ['dna_account_shown', () => ({})],
  ['dna_account_done', (p) => (['email', 'google'].includes(p.method) ? { method: p.method } : null)],
]);

const analyticsSdk = () => typeof window !== 'undefined' && window.posthog?.__midAnalyticsLoaded
  && typeof window.posthog.capture === 'function' ? window.posthog : null;

export function captureFunnelEvent(name, props = {}) {
  // mid-wi8v: Fathom first, before the PostHog consent and SDK checks; it takes names only.
  sendFathomEvent(name === 'dna_step_viewed' ? (DNA_STEPS.includes(props?.step) ? `dna_step_${props.step}` : '') : name);
  const sdk = analyticsSdk();
  const sessionId = sdk?.get_session_id?.();
  if (!sdk || !sessionId || !isCookieAllowed('analytics')
      || sdk.has_opted_out_capturing?.()
      || !['visitor', 'buyer'].includes(funnelSubject.actor)) return false;

  let clean = {};
  let sessions = [];
  try { sessions = JSON.parse(localStorage.getItem(SESSION_KEY)) || []; } catch { /* discard invalid storage */ }
  if (!Array.isArray(sessions)) sessions = [];
  let keys = [];
  if (DNA_FUNNEL_PROPS.has(name)) {
    const allowed = DNA_FUNNEL_PROPS.get(name)(props || {});
    if (!allowed) return false;
    const entryPoint = getEntryPoint();
    clean = { ...allowed, ...(entryPoint ? { entry_point: entryPoint } : {}) };
  } else if (name === 'dna_started') {
    // ponytail: any token-shaped value (comecar, landing_*, ...); free text never reaches PostHog
    const entryPoint = props.entry_point || getEntryPoint();
    if (!/^[a-z0-9_]{1,40}$/.test(entryPoint || '')) return false;
    const anonId = /^anon_[A-Za-z0-9_-]{1,59}$/.test(props.anon_id || '') ? props.anon_id : null;
    const subjects = [funnelSubject.userId, anonId].filter(Boolean);
    if (!subjects.length) return false;
    keys = subjects.map((subject) => `${DNA_PREFIX}${encodeURIComponent(subject)}`);
    clean = { ...(!funnelSubject.userId && anonId ? { anon_id: anonId } : {}), entry_point: entryPoint };
  } else if (name !== 'site_session_started' || sessions.includes(sessionId)) return false;
  if (keys.some((key) => localStorage.getItem(key))) return false;

  sdk.capture(name, {
    schema_version: 1, source_layer: 'frontend',
    identity_type: funnelSubject.userId ? 'account' : 'anonymous',
    actor_type: name.startsWith('dna_') ? 'buyer' : funnelSubject.actor,
    consent_version: CONSENT_VERSION, $session_id: sessionId, ...clean,
  });
  if (name === 'site_session_started') localStorage.setItem(SESSION_KEY, JSON.stringify([...sessions, sessionId].slice(-50)));
  keys.forEach((key) => localStorage.setItem(key, '1'));
  return true;
}

// Map home events carry only these properties, each validated: no property
// ids, no coordinates, no URLs (docs/ANALYTICS.md, map home section).
const MAP_PROPS = {
  layout: (v) => v === 'mobile' || v === 'desktop',
  pins: (v) => Number.isInteger(v) && v >= 0,
  action: (v) => ['route', 'save', 'visit', 'chat', 'detail', 'portal'].includes(v),
  mode: (v) => v === 'car' || v === 'foot',
  on: (v) => typeof v === 'boolean',
  score_band: (v) => ['teal', 'purple', 'gold', 'dim'].includes(v),
};

export function captureMapEvent(name, props = {}) {
  const sdk = analyticsSdk();
  const sessionId = sdk?.get_session_id?.();
  if (!MAP_EVENTS.has(name) || !sdk || !sessionId || !isCookieAllowed('analytics')
      || sdk.has_opted_out_capturing?.() || funnelSubject.actor !== 'buyer') return false;
  const clean = Object.fromEntries(Object.entries(props).filter(([key, value]) => MAP_PROPS[key]?.(value)));
  sdk.capture(name, {
    schema_version: 1, source_layer: 'frontend',
    identity_type: funnelSubject.userId ? 'account' : 'anonymous',
    actor_type: 'buyer', consent_version: CONSENT_VERSION, $session_id: sessionId, ...clean,
  });
  return true;
}

export function registerAnalyticsSession(sdk) {
  if (!isCookieAllowed('analytics') || typeof sdk?.capture !== 'function') return;
  if (!sdk.__midSiteSessionRegistered) {
    sdk.__midSiteSessionRegistered = true;
    sdk.onSessionId?.(() => captureFunnelEvent('site_session_started'));
  }
  captureFunnelEvent('site_session_started');
}

export function observeSiteSession(actorType, userId = null) {
  funnelSubject = ['visitor', 'buyer'].includes(actorType)
    ? { actor: actorType, userId: actorType === 'buyer' && userId ? String(userId) : null }
    : { actor: null, userId: null };
  setFathomActor(funnelSubject.actor);
  scheduleFathomFlush();
  captureFunnelEvent('site_session_started');
}

export function identifyBuyer(userId) {
  const sdk = analyticsSdk();
  const buyerId = typeof userId === 'string' ? userId.trim() : '';
  if (sdk && isCookieAllowed('analytics') && buyerId) sdk.identify(buyerId);
}

export function resetAnalytics() {
  const sdk = analyticsSdk();
  if (sdk && isCookieAllowed('analytics')) sdk.reset?.();
}

export function syncAnalyticsConsent(choice = readCookieConsent()) {
  if (choice?.version !== CONSENT_VERSION) return;
  try {
    const sdk = analyticsSdk();
    const anonId = localStorage.getItem('mid_anon_id');
    const validAnonId = /^anon_[A-Za-z0-9_-]{1,59}$/.test(anonId || '') ? anonId : null;
    if (!choice.analytics) {
      sdk?.opt_out_capturing?.();
      sdk?.stopSessionRecording?.();
    }
    const distinctId = sdk?.get_distinct_id?.();
    const body = {
      allowed: choice.analytics === true, consent_version: CONSENT_VERSION,
      ...(choice.timestamp ? { choice_timestamp: choice.timestamp } : {}),
      ...(validAnonId ? { anon_id: validAnonId } : {}),
      ...(distinctId ? { anonymous_distinct_id: distinctId } : {}),
    };
    axios.post(`${API_URL}/api/analytics/consent`, body, { withCredentials: true, timeout: 4000 }).then(({ data }) => {
      if (data?.allowed === false) {
        sdk?.opt_out_capturing?.();
        sdk?.stopSessionRecording?.();
      }
    }).catch(() => {});
  } catch {
    /* silent */
  }
}

export function trackEvent(event, props = {}) {
  // Use fetch with keepalive so it fires even during navigation
  try {
    axios.post(
      `${API_URL}/api/analytics/event`,
      { event, props },
      { withCredentials: true, timeout: 4000 }
    ).catch(() => { /* silent */ });
  } catch {
    /* silent */
  }
}

export default trackEvent;
